import { ChangeDetectorRef, Component, Inject, NgZone, OnInit, DestroyRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AccountManagementAuthenticationModel,
  AccessGroupApiResponse,
  ModifyUserRoleModel,
  UserToAddModel,
  ZenityUserRoleEnum,
} from 'src/app/models/api/accountManagement.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { AccountManagementHttpService } from 'src/app/services/httpServices/accountManagementHttpService/account-management-http-service.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';

export interface IUserDialogInput {
  mode: 'invite' | 'edit';
  authentications: AccountManagementAuthenticationModel[];
  user?: {
    userId: string;
    email: string;
    name: string;
    isAdmin: boolean;
    accessGroupIds: string[];
  };
}

export interface IUserDialogResult {
  success: boolean;
  action: 'invite' | 'edit';
  data?: {
    email: string;
    isAdmin: boolean;
    accessGroupIds: string[];
  };
}

// Keep the old interface for backward compatibility
export interface IInviteUserDialogInput {
  authentications: AccountManagementAuthenticationModel[];
}

@Component({
  selector: 'app-invite-user-dialog',
  templateUrl: './invite-user-dialog.component.html',
  styleUrls: ['./invite-user-dialog.component.scss'],
})
export class InviteUserDialogComponent implements OnInit {
  public formGroup: FormGroup;
  public authentications: ISelectOption[] = [];
  public accessGroupOptions$ = new BehaviorSubject<ISelectOption[]>([]);
  public selectedAccessGroupIds: string[] = [];
  private _accessGroups: AccessGroupApiResponse[] = [];
  private _initialAccessGroupIds: string[] = [];

  public waitingForResponse = false;
  public isEditMode = false;
  public dialogTitle = '';
  public submitButtonText = '';
  private _initialIsAdmin = false;

  constructor(
    public dialogRef: MatDialogRef<InviteUserDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public details: IUserDialogInput,
    private readonly _fb: FormBuilder,
    private readonly _accountManagementHttpService: AccountManagementHttpService,
    private readonly _notificationService: NotificationsService,
    private readonly _destroyRef: DestroyRef,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _ngZone: NgZone
  ) {
    this.isEditMode = details.mode === 'edit';
    this.dialogTitle = this.isEditMode ? 'Edit user' : 'Invite user';
    this.submitButtonText = this.isEditMode ? 'Save changes' : 'Invite';

    this._buildSelectOptionsForAuthentications();

    let authId = '';
    if (this.authentications.length === 1) {
      authId = this.authentications[0].id;
    }

    const authenticationValidators = !this.isEditMode && this.authentications.length > 1 ? [Validators.required] : [];

    this.formGroup = this._fb.group({
      userEmail: [this.isEditMode ? details.user?.email : '', [Validators.required, Validators.email]],
      authentication_id: [authId, authenticationValidators],
      isAdmin: [this.isEditMode ? details.user?.isAdmin : false],
      accessGroups: [[]],
    });

    if (this.isEditMode && details.user) {
      this.selectedAccessGroupIds = details.user.accessGroupIds || [];
      this._initialAccessGroupIds = [...this.selectedAccessGroupIds];
      this._initialIsAdmin = details.user.isAdmin;
      this.formGroup.get('accessGroups')?.setValue(this.selectedAccessGroupIds);
    }
  }

  public get hasChanges(): boolean {
    if (!this.isEditMode) {
      return true;
    }

    const currentIsAdmin = this.formGroup.get('isAdmin')?.value ?? false;
    const isAdminChanged = currentIsAdmin !== this._initialIsAdmin;

    const accessGroupsChanged =
      this.selectedAccessGroupIds.length !== this._initialAccessGroupIds.length ||
      this.selectedAccessGroupIds.some((id) => !this._initialAccessGroupIds.includes(id));

    return isAdminChanged || accessGroupsChanged;
  }

  ngOnInit(): void {
    this._loadAccessGroups();
  }

  public onClose(): void {
    this.dialogRef.close({ success: false } as IUserDialogResult);
  }

  public onAccessGroupSelected(selected: ISelectOption[]): void {
    this.selectedAccessGroupIds = selected.map((o) => o.id);
    this.formGroup.get('accessGroups')?.setValue(this.selectedAccessGroupIds);
  }

  public submit(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    if (this.isEditMode) {
      this._updateUser();
    } else {
      this._inviteUser();
    }
  }

  private _inviteUser(): void {
    this.waitingForResponse = true;
    const isAdmin = this.formGroup.get('isAdmin')?.value;
    const role: ZenityUserRoleEnum = isAdmin ? ZenityUserRoleEnum.admin : ZenityUserRoleEnum.viewer;
    const userEmail = this.formGroup.get('userEmail')?.value;

    this._accountManagementHttpService
      .inviteUser({
        email: userEmail,
        role: role,
        authentication_id: this.formGroup.get('authentication_id')?.value,
      } as UserToAddModel)
      .subscribe({
        next: () => {
          if (this.selectedAccessGroupIds.length > 0 && !isAdmin) {
            this._assignInvitedUserToAccessGroups(userEmail, isAdmin);
          } else {
            this._closeInviteDialogWithSuccess(userEmail, isAdmin);
          }
        },
        error: (error) => {
          if (error.status === 422) {
            const detail: unknown = error?.error?.detail;
            if (typeof detail === 'string' && detail.toLowerCase().includes('is not allowed in the account')) {
              this._notificationService.warning(
                'The email ' + userEmail + ' domain of this invitee is not allowed. Please use an approved domain.'
              );
            } else {
              this._notificationService.warning('User ' + userEmail + ' already invited or a member');
            }
          }
          this.waitingForResponse = false;
        },
      });
  }

  private _assignInvitedUserToAccessGroups(userEmail: string, isAdmin: boolean): void {
    const updateObservables = [];

    for (const groupId of this.selectedAccessGroupIds) {
      const group = this._accessGroups.find((g) => g.id === groupId);
      if (group) {
        const updatedUsers = group.users.includes(userEmail) ? group.users : [...group.users, userEmail];
        updateObservables.push(this._accountManagementHttpService.updateAccessGroup(groupId, { users: updatedUsers }));
      }
    }

    if (updateObservables.length === 0) {
      this._closeInviteDialogWithSuccess(userEmail, isAdmin);
      return;
    }

    forkJoin(updateObservables).subscribe({
      next: () => {
        this._closeInviteDialogWithSuccess(userEmail, isAdmin);
      },
      error: () => {
        this._notificationService.warning('Invitation sent but failed to assign access groups');
        this._closeInviteDialogWithSuccess(userEmail, isAdmin);
      },
    });
  }

  private _closeInviteDialogWithSuccess(userEmail: string, isAdmin: boolean): void {
    this._notificationService.success('Invitation sent successfully');
    this.dialogRef.close({
      success: true,
      action: 'invite',
      data: {
        email: userEmail,
        isAdmin: isAdmin,
        accessGroupIds: this.selectedAccessGroupIds,
      },
    } as IUserDialogResult);
  }

  private _updateUser(): void {
    const userId = this.details.user?.userId;
    const userEmail = this.details.user?.email;
    if (!userId || !userEmail) {
      this._notificationService.warning('User information is missing');
      return;
    }

    this.waitingForResponse = true;
    const isAdmin = this.formGroup.get('isAdmin')?.value;
    const newRole: ZenityUserRoleEnum = isAdmin ? ZenityUserRoleEnum.admin : ZenityUserRoleEnum.viewer;

    const modifyRoleModel: ModifyUserRoleModel = {
      userId: userId,
      newRole: newRole,
    };

    this._accountManagementHttpService.modifyRole(modifyRoleModel).subscribe({
      next: () => {
        this._updateUserAccessGroups(userEmail);
      },
      error: () => {
        this._notificationService.warning('Failed to update user role');
        this.waitingForResponse = false;
      },
    });
  }

  private _updateUserAccessGroups(userEmail: string): void {
    const groupsToAddUserTo = this.selectedAccessGroupIds.filter((id) => !this._initialAccessGroupIds.includes(id));
    const groupsToRemoveUserFrom = this._initialAccessGroupIds.filter(
      (id) => !this.selectedAccessGroupIds.includes(id)
    );

    const updateObservables = [];

    for (const groupId of groupsToAddUserTo) {
      const group = this._accessGroups.find((g) => g.id === groupId);
      if (group) {
        const updatedUsers = group.users.includes(userEmail) ? group.users : [...group.users, userEmail];
        updateObservables.push(this._accountManagementHttpService.updateAccessGroup(groupId, { users: updatedUsers }));
      }
    }

    for (const groupId of groupsToRemoveUserFrom) {
      const group = this._accessGroups.find((g) => g.id === groupId);
      if (group) {
        const updatedUsers = group.users.filter((u) => u !== userEmail);
        updateObservables.push(this._accountManagementHttpService.updateAccessGroup(groupId, { users: updatedUsers }));
      }
    }

    if (updateObservables.length === 0) {
      this._closeDialogWithSuccess();
      return;
    }

    forkJoin(updateObservables).subscribe({
      next: () => {
        this._closeDialogWithSuccess();
      },
      error: () => {
        this._notificationService.warning('Failed to update user access groups');
        this.waitingForResponse = false;
      },
    });
  }

  private _closeDialogWithSuccess(): void {
    const isAdmin = this.formGroup.get('isAdmin')?.value;
    this._notificationService.success('User updated successfully');
    this.dialogRef.close({
      success: true,
      action: 'edit',
      data: {
        email: this.formGroup.get('userEmail')?.value,
        isAdmin: isAdmin,
        accessGroupIds: this.selectedAccessGroupIds,
      },
    } as IUserDialogResult);
  }

  private _buildSelectOptionsForAuthentications(): void {
    const authenticationsSelectOptions: ISelectOption[] = [];
    this.details.authentications.forEach((authentication) => {
      authenticationsSelectOptions.push({
        id: authentication.id,
        value: authentication.id,
        displayName: authentication.name,
      });
    });
    this.authentications = authenticationsSelectOptions;
  }

  private _loadAccessGroups(): void {
    this._accountManagementHttpService
      .getAccessGroups()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((response) => {
        const items = response._embedded?.items || [];
        this._accessGroups = items;
        const options: ISelectOption[] = items.map((group) => ({
          id: group.id,
          value: group.id,
          displayName: group.name,
        }));

        if (this.isEditMode && this.details.user?.accessGroupIds) {
          // Ensure selectedAccessGroupIds is set before emitting options
          this.selectedAccessGroupIds = [...this.details.user.accessGroupIds];
          this._cdr.detectChanges();
          this._ngZone.runOutsideAngular(() => {
            setTimeout(() => {
              this._ngZone.run(() => {
                this.accessGroupOptions$.next(options);
                this._cdr.detectChanges();
              });
            });
          });
        } else {
          this.accessGroupOptions$.next(options);
        }
      });
  }
}
